import React, { useState, useEffect, useCallback } from 'react';
import './FeatureReel.scss';

export interface ReelSlide {
  image: string;
  caption: string;
}

export interface FeatureReelItem {
  title: string;
  slides: ReelSlide[];
  onClick?: () => void;
}

interface FeatureReelProps {
  items: FeatureReelItem[];
  slideInterval?: number;
}

const ReelCard: React.FC<{ item: FeatureReelItem; slideInterval: number }> = ({
  item,
  slideInterval,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => {
    setActiveIndex((i) => (i + 1) % item.slides.length);
  }, [item.slides.length]);

  useEffect(() => {
    if (paused || item.slides.length <= 1) return;
    const timer = setInterval(next, slideInterval);
    return () => clearInterval(timer);
  }, [paused, next, slideInterval, item.slides.length]);

  return (
    <div
      className="reel-card"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onClick={item.onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && item.onClick?.()}
      aria-label={`Feature reel: ${item.title}`}
    >
      <div className="reel-card__viewport">
        {item.slides.map((slide, i) => (
          <div
            key={i}
            className={`reel-card__slide ${i === activeIndex ? 'reel-card__slide--active' : ''}`}
          >
            <img src={slide.image} alt={slide.caption} />
            <div className="reel-card__caption">{slide.caption}</div>
          </div>
        ))}
      </div>
      <div className="reel-card__title">{item.title}</div>
      <div className="reel-card__dots">
        {item.slides.map((_, i) => (
          <button
            key={i}
            className={`reel-card__dot ${i === activeIndex ? 'reel-card__dot--active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setActiveIndex(i);
            }}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export const FeatureReel: React.FC<FeatureReelProps> = ({ items, slideInterval = 2500 }) => {
  return (
    <div className="feature-reel">
      <div className="feature-reel__header">🔥 What's New</div>
      <div className="feature-reel__track">
        {items.map((item, i) => (
          <ReelCard key={i} item={item} slideInterval={slideInterval} />
        ))}
      </div>
    </div>
  );
};
